import { AlignLeft, Loader2, Mic, MicOff, RotateCcw, Sparkles, Trash2, Undo2, Wand2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '../Button'
import { Modal } from '../Modal'
import { Text } from '../Text'
import type { DictationTone } from './dictation.types'

interface DictationControlsProps {
	disabled?: boolean
	allowRewrite?: boolean
	isListening: boolean
	isProcessing?: boolean
	error?: string | null
	interimTranscript?: string
	draftTranscript?: string
	canUndoRewrite?: boolean
	onStart: () => void
	onStop: () => void
	onInsert: () => void
	onReplace: () => void
	onDiscard: () => void
	onReset: () => void
	onRewrite: (tone: DictationTone) => void
	onUndoRewrite: () => void
}

const TONE_OPTIONS: { tone: DictationTone; label: string; description: string }[] = [
	{ tone: 'concise', label: 'Concise', description: 'Shorter & clearer' },
	{ tone: 'professional', label: 'Professional', description: 'Polished & formal' },
	{ tone: 'empathetic', label: 'Empathetic', description: 'Warm & supportive' },
]

function countWords(text: string): number {
	return text.split(/\s+/).filter(Boolean).length
}

export function DictationControls({
	disabled,
	allowRewrite = false,
	isListening,
	isProcessing = false,
	error,
	interimTranscript,
	draftTranscript,
	canUndoRewrite = false,
	onStart,
	onStop,
	onInsert,
	onReplace,
	onDiscard,
	onReset,
	onRewrite,
	onUndoRewrite,
}: DictationControlsProps) {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [showCloseConfirm, setShowCloseConfirm] = useState(false)
	const scrollRef = useRef<HTMLDivElement>(null)

	const hasDraft = !!(draftTranscript?.trim() || interimTranscript?.trim())
	const fullText = [draftTranscript, interimTranscript].filter(Boolean).join(' ')
	const totalWords = countWords(fullText)

	const handleOpenModal = () => {
		setIsModalOpen(true)
		setShowCloseConfirm(false)
		if (!isListening) {
			onStart()
		}
	}

	const forceClose = useCallback(() => {
		setShowCloseConfirm(false)
		setIsModalOpen(false)
		if (isListening) {
			onStop()
		}
		onDiscard()
	}, [isListening, onDiscard, onStop])

	const handleCloseModal = useCallback(() => {
		if (hasDraft) {
			setShowCloseConfirm(true)
			return
		}
		setIsModalOpen(false)
		if (isListening) {
			onStop()
		}
	}, [hasDraft, isListening, onStop])

	// biome-ignore lint/correctness/useExhaustiveDependencies: draftTranscript and interimTranscript are intentional triggers to auto-scroll on new content
	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight
		}
	}, [draftTranscript, interimTranscript])

	return (
		<div className='w-full flex flex-col'>
			<div className='flex items-center justify-end'>
				<button
					type='button'
					className='inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors disabled:opacity-40 cursor-pointer'
					onClick={handleOpenModal}
					disabled={disabled || isProcessing}
					aria-label='Open dictation'>
					<Mic size={13} />
					Dictate
				</button>
			</div>

			<Modal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				closeOnOverlayClick={!hasDraft}
				title='Dictation Assistant'>
				<div className='flex flex-col gap-4'>
					{/* Close confirmation banner */}
					{showCloseConfirm && (
						<div className='flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200'>
							<Text variant='xsmall' className='text-amber-800'>
								You have unsaved text. Discard it?
							</Text>
							<div className='flex gap-2 shrink-0'>
								<button
									type='button'
									className='px-2.5 py-1 text-xs font-medium text-neutral-700 bg-neutral-0 border border-neutral-300 rounded-md hover:bg-neutral-50 cursor-pointer'
									onClick={() => setShowCloseConfirm(false)}>
									Keep editing
								</button>
								<button
									type='button'
									className='px-2.5 py-1 text-xs font-medium text-error-700 bg-error-50 border border-error-200 rounded-md hover:bg-error-100 cursor-pointer'
									onClick={forceClose}>
									Discard
								</button>
							</div>
						</div>
					)}

					{/* Mic section */}
					<div className='flex flex-col items-center gap-2 pt-1 pb-2'>
						<button
							type='button'
							onClick={isListening ? onStop : onStart}
							disabled={disabled || isProcessing}
							className='relative h-[72px] w-[72px] rounded-full flex items-center justify-center transition-all cursor-pointer focus:outline-none disabled:opacity-40'
							style={{
								background: isListening
									? 'linear-gradient(135deg, var(--color-primary-100), var(--color-primary-200))'
									: '#f5f5f5',
							}}
							aria-label={isListening ? 'Pause recording' : 'Start recording'}>
							{isProcessing ? (
								<Loader2 size={26} className='text-primary-600 animate-spin' />
							) : isListening ? (
								<MicOff size={26} className='text-primary-700' />
							) : (
								<Mic size={26} className='text-neutral-400' />
							)}
						</button>

						<Text variant='hint' className={isListening ? 'text-primary-600 font-medium' : 'text-neutral-400'}>
							{isProcessing
								? 'Rewriting...'
								: isListening
									? 'Tap to pause'
									: hasDraft
										? 'Paused — tap to continue'
										: 'Tap to start'}
						</Text>
					</div>

					{/* Transcript area */}
					<div className='rounded-xl border border-neutral-200 bg-white overflow-hidden'>
						{/* Header — only visible when there's text */}
						{hasDraft && (
							<div className='flex items-center justify-between px-3.5 py-1.5 border-b border-neutral-100'>
								<Text variant='hint' className='text-neutral-400 font-medium'>
									Transcript
								</Text>
								<Text variant='hint' className='text-neutral-400 tabular-nums'>
									{totalWords} word{totalWords !== 1 ? 's' : ''}
								</Text>
							</div>
						)}

						{/* Text body */}
						<div ref={scrollRef} className='px-4 py-3.5 min-h-[130px] max-h-[240px] overflow-y-auto scroll-smooth'>
							{!hasDraft ? (
								<div className='flex items-center justify-center h-[110px]'>
									<Text variant='hint' className='text-neutral-300 italic'>
										{isListening ? 'Speak now...' : 'Your transcript will appear here'}
									</Text>
								</div>
							) : (
								<div aria-live='polite'>
									<p className='text-[14px] leading-[1.7] tracking-[-0.01em]'>
										{draftTranscript && <span className='text-neutral-800'>{draftTranscript}</span>}
										{draftTranscript && interimTranscript && ' '}
										{interimTranscript && <span className='text-primary-500'>{interimTranscript}</span>}
										{isListening && (
											<span
												className='inline-block w-[2px] h-[15px] rounded-full ml-[2px] align-text-bottom'
												style={{
													background: 'var(--color-primary-500)',
													animation: 'cursor-blink 1s step-end infinite',
												}}
											/>
										)}
									</p>
									<style>{`
                    @keyframes cursor-blink {
                      0%, 100% { opacity: 1; }
                      50% { opacity: 0; }
                    }
                  `}</style>
								</div>
							)}
						</div>
					</div>

					{error && (
						<div className='rounded-lg bg-error-50 border border-error-200 px-3 py-2'>
							<Text variant='hint' className='text-error-700'>
								{error}
							</Text>
						</div>
					)}

					{/* Rewrite actions + undo */}
					{allowRewrite && hasDraft && (
						<div className='flex flex-col gap-2'>
							<div className='flex items-center justify-between'>
								<div className='flex items-center gap-1.5'>
									<Sparkles size={12} className='text-primary-600' />
									<Text variant='hint' className='text-neutral-600 font-medium'>
										Rewrite with AI
									</Text>
								</div>
								{canUndoRewrite && (
									<button
										type='button'
										className='inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium cursor-pointer transition-colors'
										onClick={onUndoRewrite}>
										<Undo2 size={12} />
										Undo rewrite
									</button>
								)}
							</div>
							<div className='flex gap-2'>
								{TONE_OPTIONS.map(({ tone, label, description }) => (
									<button
										key={tone}
										type='button'
										className='flex-1 flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg border border-neutral-200 bg-neutral-0 hover:border-primary-300 hover:bg-primary-50 active:scale-[0.98] transition-all disabled:opacity-40 cursor-pointer'
										onClick={() => onRewrite(tone)}
										disabled={disabled || isProcessing}>
										<div className='flex items-center gap-1'>
											<Wand2 size={11} className='text-primary-600' />
											<Text variant='hint' className='font-medium text-neutral-800'>
												{label}
											</Text>
										</div>
										<Text variant='hint' className='text-neutral-500 text-[10px]'>
											{description}
										</Text>
									</button>
								))}
							</div>
						</div>
					)}

					{/* Actions */}
					<div className='flex items-center justify-between pt-2 border-t border-neutral-100'>
						<div className='flex gap-1'>
							<Button
								variant='tertiary'
								size='small'
								onClick={() => {
									onDiscard()
									setShowCloseConfirm(false)
									setIsModalOpen(false)
								}}>
								<span className='flex items-center gap-1'>
									<Trash2 size={13} />
									Discard
								</span>
							</Button>
							<Button variant='tertiary' size='small' onClick={onReset} disabled={!hasDraft && !isListening}>
								<span className='flex items-center gap-1'>
									<RotateCcw size={13} />
									Reset
								</span>
							</Button>
						</div>

						<div className='flex gap-2'>
							<Button
								variant='secondary'
								size='small'
								onClick={() => {
									onInsert()
									setShowCloseConfirm(false)
									setIsModalOpen(false)
								}}
								disabled={!hasDraft}>
								<span className='flex items-center gap-1'>
									<AlignLeft size={13} />
									Append
								</span>
							</Button>
							<Button
								variant='primary'
								size='small'
								onClick={() => {
									onReplace()
									setShowCloseConfirm(false)
									setIsModalOpen(false)
								}}
								disabled={!hasDraft}>
								Replace
							</Button>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	)
}
